import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TECH_CHART_STATE_KEYS } from 'ChartApp/ts/features/tech-chart/constants';
import { TechChartFlatAppState } from 'ChartApp/ts/types/tech-chart-common.type';
import { TechChartState, TechChartViewResponse } from '../interfaces/tech-chart.interface';
import { LooseObject } from 'Angular/interfaces/loose-object.interface';

import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';

import { convertNestedStringToBoolean } from 'Angular/utils/state-utils';
import { formFlattenedObject, formFlattenedParamString } from 'Angular/utils/urls';

import { TechChartUrlSerializer } from 'ChartApp/ts/features/tech-chart-page-shared/state/tech-chart.serializer';

@Injectable({
    providedIn: 'root',
})
export class TechChartAjaxService {
    serializedKeys: string[] = [];

    constructor(private ajaxService: AjaxService, private urlsService: UrlsService) {
        this.serializedKeys = TECH_CHART_STATE_KEYS;
    }

    getFlattenedFormData(techChartState: TechChartState): TechChartFlatAppState {
        return formFlattenedObject(techChartState, this.serializedKeys, TechChartUrlSerializer);
    }

    getChartInfo(flatTechChartState: TechChartFlatAppState): Observable<TechChartViewResponse> {
        const chartDataBaseUrl = this.urlsService.CHARTS.TECH_CHART_DATA.json;
        const postParams = { ...flatTechChartState };
        return this.ajaxService
            .postFormEncodedDataObserveBody<TechChartViewResponse>(chartDataBaseUrl, postParams)
            .pipe(
                map((response: LooseObject) => {
                    convertNestedStringToBoolean(response);
                    return response as TechChartViewResponse;
                })
            );
    }

    /**
     * Get image source url for making a request to the chart generator service.
     *
     * @param _params {TechChartFlatAppState}
     * @param embedType {string}
     */
    getChartEmbedUrl(_params: TechChartFlatAppState, embedType: string): string {
        const urlExtension = this.urlsService.getUrl(this.urlsService.EMBEDS.TECH_CHART, { mode: embedType });
        const queryParams = this.getChartDataUrlQuery(_params);
        return `${process.env.NODE_BASE_URL}${urlExtension}&${queryParams}`;
    }

    getChartShareUrl(baseShareUrl: string, techChartState: TechChartState): Observable<{ redirectUrl: string }> {
        const data = this.getFlattenedFormData(techChartState);
        return this.ajaxService.postFormEncodedDataObserveBody<{ redirectUrl: string }>(baseShareUrl, data);
    }

    getChartDataUrlQuery(techChartFlatAppState: TechChartFlatAppState): string {
        const state = { ...techChartFlatAppState };
        return formFlattenedParamString(state, TECH_CHART_STATE_KEYS, true);
    }

    getChartSaveAsImageUrl(techChartState: TechChartFlatAppState): Observable<HttpResponse<{ image_url: string }>> {
        const baseUrl = this.urlsService.EMBEDS.SAVE_AS_IMAGE_TECH_CHART;
        const state = { ...techChartState };
        const data = formFlattenedObject(state, TECH_CHART_STATE_KEYS, TechChartUrlSerializer);
        return this.ajaxService.postFormEncodedDataObserveResponse<{ image_url: string }>(baseUrl, data);
    }

    getChartDisclosure(techChartState: TechChartState): Observable<HttpResponse<string>> {
        const url = this.urlsService.DISCLOSURES.TECH_CHART_DISCLOSURE;
        const state = { ...techChartState };
        const data = formFlattenedObject(state, TECH_CHART_STATE_KEYS);
        return this.ajaxService.postFormEncodedDataWithTextObserveResponse(url, data);
    }

    getChartCSVDataFromState(techChartState: TechChartState): Observable<HttpResponse<Blob>> {
        const chartDataBaseUrl = this.urlsService.CHARTS.TECH_CHART_DATA.csv;
        const state = { ...techChartState };
        const data = formFlattenedObject(state, this.serializedKeys);
        return this.ajaxService.postFormEncodedDataWithBlobObserveResponse(chartDataBaseUrl, data);
    }
}
